// src/components/dms/oms/field-item-form-config/FieldItemFormConfig.jsx
import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    Typography, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, IconButton, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Button, Snackbar, Alert,
    CircularProgress, Menu, MenuItem,
    Chip,
    Autocomplete,
    FormControlLabel, Checkbox,
    Select, FormControl, InputLabel
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from '@mui/icons-material/Close';

import { database } from "../../../../firebase";
import { ref, push, set, onValue, update, remove, query, orderByChild, equalTo } from "firebase/database";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

function FieldItemFormConfig({ title, dbPath, fieldName, primaryKeyField }) {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [fieldValue, setFieldValue] = useState("");
    const [parentValue, setParentValue] = useState(null);
    const [parentList, setParentList] = useState([]);
    const [parentValues, setParentValues] = useState({});
    const [parentOptions, setParentOptions] = useState({});

    // State baru untuk Field Item Form
    const [propertiFieldValue, setPropertiFieldValue] = useState("");
    const [useFieldItemForm, setUseFieldItemForm] = useState(false);
    const [dataTypeValue, setDataTypeValue] = useState("Text");
    const [isFormulaValue, setIsFormulaValue] = useState("False");
    const [formulaValue, setFormulaValue] = useState("");

    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const [indexValue, setIndexValue] = useState("");
    const [namaFormOptions, setNamaFormOptions] = useState([]);
    const [selectedNamaForm, setSelectedNamaForm] = useState(null);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    const fileInputRef = useRef(null);

// Fungsi helper untuk menentukan path dan field yang relevan untuk multi-parent
const getMultiParentConfig = () => {
    if (title.includes("Field Item Form")) {
        return [
            {
                label: "Kategori",
                key: "kategori_form",
                dbPath: "db_config/db_observation/tb_kategori_form",
                labelField: "nama_kategori_form",
                idField: "id_kategori_form",
                parentRef: "id_form", // PERUBAHAN: dari "id_nama_form" menjadi "id_form"
                parentDbPath: "db_config/db_observation/tb_nama_form", // Path untuk parent
                parentLabelField: "nama_form" // Field untuk label parent
            },
            {
                label: "Sub Kategori",
                key: "sub_kategori_form",
                dbPath: "db_config/db_observation/tb_sub_kategori_form",
                labelField: "nama_sub_kategori_form",
                idField: "id_sub_kategori_form",
                parentRef: "id_kategori_form", // Field yang merujuk ke parent
                parentDbPath: "db_config/db_observation/tb_kategori_form", // Path untuk parent
                parentLabelField: "nama_kategori_form" // Field untuk label parent
            }
        ];
    }
    return [];
};

    const handleIndexChange = (e) => {
        const value = e.target.value.replace(/[^0-9]/g, '');
        setIndexValue(value);
    };

    // Ambil data Nama Form saat komponen dimount
    useEffect(() => {
        if (title.includes("Field Item Form")) {
            const fetchNamaFormOptions = async () => {
                try {
                    const namaFormRef = ref(database, "db_config/db_observation/tb_nama_form");
                    const snapshot = await onValuePromise(namaFormRef);
                    const data = snapshot.val();
                    let options = [];
                    
                    if (snapshot.exists()) {
                        options = Object.values(data).map(item => ({
                            id: item.id_form,
                            label: item.nama_form
                        }));
                    }
                    
                    setNamaFormOptions(options);
                } catch (error) {
                    console.error("Gagal memuat data Nama Form:", error);
                }
            };
            
            fetchNamaFormOptions();
        }
    }, [title]);

// Reset selectedNamaForm saat dialog dibuka HANYA untuk mode tambah (bukan edit)
useEffect(() => {
    if (openDialog && title.includes("Field Item Form") && !isEditing) {
        setSelectedNamaForm(null);
    }
}, [openDialog, title, isEditing]);

// Ambil data dari Firebase
useEffect(() => {
    const tbRef = ref(database, dbPath);
    setLoading(true);

    const multiParentConfigs = getMultiParentConfig();

    const unsubscribe = onValue(tbRef, async (snapshot) => {
        const data = snapshot.val();
        let rawDataList = [];
        if (snapshot.exists()) {
            rawDataList = Object.keys(data).map(key => ({
                [primaryKeyField]: key,
                ...data[key]
            }));
        }

        const fetchParentData = async () => {
            let combinedData = [...rawDataList];

            if (multiParentConfigs.length > 0) {
                // Ambil data Nama Form terlebih dahulu
                const namaFormMap = {};
                try {
                    const namaFormRef = ref(database, "db_config/db_observation/tb_nama_form");
                    const namaFormSnapshot = await onValuePromise(namaFormRef);
                    if (namaFormSnapshot.exists()) {
                        Object.values(namaFormSnapshot.val() || {}).forEach(item => {
                            namaFormMap[item.id_form] = item.nama_form;
                        });
                    }
                } catch (error) {
                    console.error("Gagal memuat data Nama Form:", error);
                }

                // Ambil data Kategori
                const kategoriMap = {};
                try {
                    const kategoriRef = ref(database, "db_config/db_observation/tb_kategori_form");
                    const kategoriSnapshot = await onValuePromise(kategoriRef);
                    if (kategoriSnapshot.exists()) {
                        Object.values(kategoriSnapshot.val() || {}).forEach(item => {
                            kategoriMap[item.id_kategori_form] = {
                                ...item,
                                nama_form: namaFormMap[item.id_form] || "Tidak Ditemukan"
                            };
                        });
                    }
                } catch (error) {
                    console.error("Gagal memuat data Kategori:", error);
                }

                // Ambil data Sub Kategori 
                const subKategoriMap = {};
                try {
                    const subKategoriRef = ref(database, "db_config/db_observation/tb_sub_kategori_form");
                    const subKategoriSnapshot = await onValuePromise(subKategoriRef);
                    if (subKategoriSnapshot.exists()) {
                        Object.values(subKategoriSnapshot.val() || {}).forEach(item => {
                            subKategoriMap[item.id_sub_kategori_form] = {
                                ...item,
                                // Untuk sub kategori, ambil nama form melalui kategori
                                nama_form: kategoriMap[item.id_kategori_form]?.nama_form || "Tidak Ditemukan"
                            };
                        });
                    }
                } catch (error) {
                    console.error("Gagal memuat data Sub Kategori:", error);
                }

                // Proses data field item form
                combinedData = combinedData.map(item => {
                    const newItem = { ...item };
                    
                    // Handle kategori form
                    if (item.id_kategori_form) {
                        const kategori = kategoriMap[item.id_kategori_form];
                        if (kategori) {
                            newItem.nama_kategori_form = kategori.nama_kategori_form;
                            newItem.nama_form = kategori.nama_form;
                        } else {
                            newItem.nama_kategori_form = "Tidak Ditemukan";
                            newItem.nama_form = "Tidak Ditemukan";
                        }
                    }
                    
                    // Handle sub kategori form
                    if (item.id_sub_kategori_form) {
                        const subKategori = subKategoriMap[item.id_sub_kategori_form];
                        if (subKategori) {
                            newItem.nama_sub_kategori_form = subKategori.nama_sub_kategori_form;
                            // Untuk sub kategori, gunakan nama form dari sub kategori
                            newItem.nama_form = subKategori.nama_form;
                        } else {
                            newItem.nama_sub_kategori_form = "Tidak Ditemukan";
                        }
                    }
                    
                    return newItem;
                });
            }

            setDataList(combinedData);
            setLoading(false);
        };

        fetchParentData().catch(error => {
            console.error("Error:", error);
            setSnackbar({
                open: true,
                message: `Gagal memuat data ${title.toLowerCase()}.`,
                severity: "error",
            });
            setLoading(false);
        });
    }, (error) => {
        console.error("Error:", error);
        setSnackbar({
            open: true,
            message: `Gagal memuat data ${title.toLowerCase()}.`,
            severity: "error",
        });
        setLoading(false);
    });

    return () => unsubscribe();
}, [dbPath, title, primaryKeyField]);

// Ambil data parent saat dialog dibuka
useEffect(() => {
    const multiParentConfigs = getMultiParentConfig();

    if (openDialog && multiParentConfigs.length > 0) {
        const fetchParentOptions = async () => {
            // Ambil data Nama Form terlebih dahulu
            const namaFormMap = {};
            try {
                const namaFormRef = ref(database, "db_config/db_observation/tb_nama_form");
                const namaFormSnapshot = await onValuePromise(namaFormRef);
                if (namaFormSnapshot.exists()) {
                    Object.values(namaFormSnapshot.val() || {}).forEach(item => {
                        namaFormMap[item.id_form] = item.nama_form;
                    });
                }
            } catch (error) {
                console.error("Gagal memuat data Nama Form:", error);
            }

            // Ambil data Kategori untuk referensi Sub Kategori
            const kategoriMap = {};
            try {
                const kategoriRef = ref(database, "db_config/db_observation/tb_kategori_form");
                const kategoriSnapshot = await onValuePromise(kategoriRef);
                if (kategoriSnapshot.exists()) {
                    Object.values(kategoriSnapshot.val() || {}).forEach(item => {
                        kategoriMap[item.id_kategori_form] = {
                            nama_kategori_form: item.nama_kategori_form,
                            nama_form: namaFormMap[item.id_form] || "Unknown"
                        };
                    });
                }
            } catch (error) {
                console.error("Gagal memuat data Kategori:", error);
            }

            for (const config of multiParentConfigs) {
                try {
                    const parentRef = ref(database, config.dbPath);
                    const snapshot = await onValuePromise(parentRef);
                    const data = snapshot.val();
                    let list = [];
                    
                    if (snapshot.exists()) {
                        list = Object.values(data).map(item => {
                            let label = item[config.labelField];
                            
                            // Format label untuk Kategori: Kategori - Nama Form
                            if (config.key === "kategori_form" && config.parentRef) {
                                const namaForm = namaFormMap[item[config.parentRef]] || "Unknown";
                                label = `${label} - ${namaForm}`;
                            }
                            
                            // Format label untuk Sub Kategori: Sub Kategori - Kategori - Nama Form
                            if (config.key === "sub_kategori_form" && config.parentRef) {
                                const kategori = kategoriMap[item[config.parentRef]];
                                if (kategori) {
                                    label = `${label} - ${kategori.nama_kategori_form} - ${kategori.nama_form}`;
                                } else {
                                    label = `${label} - Unknown - Unknown`;
                                }
                            }
                            
                            return {
                                id: item[config.idField],
                                label: label,
                                originalData: item
                            };
                        });
                    }
                    
                    setParentOptions(prev => ({
                        ...prev,
                        [config.key]: [{ id: null, label: "None" }, ...list]
                    }));
                } catch (error) {
                    console.error(`Gagal memuat data ${config.label}:`, error);
                }
            }
        };

        fetchParentOptions();
    }
}, [openDialog, title]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenDialog = () => {
        setIsEditing(false);
        setFieldValue("");
        setParentValue(null);
        setParentValues({});
        setPropertiFieldValue("");
        setUseFieldItemForm(false);
        setDataTypeValue("Text");
        setIsFormulaValue("False");
        setFormulaValue("");
        setIndexValue("");
        setSelectedNamaForm(null);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setFieldValue("");
        setParentValue(null);
        setParentValues({});
        setPropertiFieldValue("");
        setUseFieldItemForm(false);
        setDataTypeValue("Text");
        setIsFormulaValue("False");
        setFormulaValue("");
        setIndexValue("");
        setSelectedNamaForm(null);
        setIsEditing(false);
        setCurrentId(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleSave = async () => {
        const multiParentConfigs = getMultiParentConfig();

        if (!fieldValue.trim()) {
            setSnackbar({
                open: true,
                message: `${title.slice(title.indexOf(" ") + 1)} tidak boleh kosong.`,
                severity: "warning",
            });
            return;
        }

        // Validasi khusus untuk Field Item Form: pastikan Nama Form dipilih
    if (title.includes("Field Item Form") && !selectedNamaForm) {
        setSnackbar({
            open: true,
            message: "Nama Form harus dipilih.",
            severity: "warning",
        });
        return;
    }
        
        let isDuplicate = false;

        // Validasi khusus untuk 'Field Item Form'
        if (title.includes("Field Item Form")) {
            if (!propertiFieldValue.trim()) {
                setSnackbar({
                    open: true,
                    message: "Properti Field tidak boleh kosong.",
                    severity: "warning",
                });
                return;
            }

            const selectedKategoriId = parentValues.kategori_form?.id || null;
            const normalizedPropertiFieldValue = propertiFieldValue.toLowerCase().trim();

            isDuplicate = dataList.some(item =>
                item.properti_field?.toLowerCase().trim() === normalizedPropertiFieldValue &&
                item.id_kategori_form === selectedKategoriId &&
                item[primaryKeyField] !== currentId
            );
            
        } else {
            const normalizedFieldValue = fieldValue.toLowerCase().trim();
            isDuplicate = dataList.some(item =>
                item[fieldName]?.toLowerCase().trim() === normalizedFieldValue &&
                item[primaryKeyField] !== currentId
            );
        }

        if (isDuplicate) {
            setSnackbar({
                open: true,
                message: `${title.slice(title.indexOf(" ") + 1)} "${propertiFieldValue}" sudah ada untuk kategori ini.`,
                severity: "error",
            });
            return;
        }

        try {
            let dataToSave = {
                [fieldName]: fieldValue,
            };

            if (indexValue) {
                dataToSave.index = indexValue;
            }

            // TAMBAHKAN ID_FORM JIKA NAMA FORM DIPILIH
        if (title.includes("Field Item Form") && selectedNamaForm) {
            dataToSave.id_form = selectedNamaForm.id;
        }

            // Tambahkan id parent (multi) jika ada
            if (multiParentConfigs.length > 0) {
                multiParentConfigs.forEach(config => {
                    const selectedParent = parentValues[config.key];
                    dataToSave[config.idField] = selectedParent?.id || null;
                });
                
                dataToSave.properti_field = propertiFieldValue;
                dataToSave.data_type = dataTypeValue;
                dataToSave.is_formula = isFormulaValue === "True";
                dataToSave.formula = formulaValue;
            }

            if (isEditing) {
                const itemRef = ref(database, `${dbPath}/${currentId}`);
                await update(itemRef, dataToSave);
                setSnackbar({
                    open: true,
                    message: `${title.slice(title.indexOf(" ") + 1)} berhasil diperbarui.`,
                    severity: "success",
                });
            } else {
                const tbRef = ref(database, dbPath);
                const newRef = push(tbRef);
                const key = newRef.key;
                const finalData = {
                    ...dataToSave,
                    [primaryKeyField]: key,
                };
                await set(newRef, finalData);
                setSnackbar({
                    open: true,
                    message: `${title.slice(title.indexOf(" ") + 1)} berhasil disimpan.`,
                    severity: "success",
                });
            }
            handleCloseDialog();
        } catch (error) {
            console.error("Gagal simpan/update:", error);
            setSnackbar({
                open: true,
                message: `Gagal menyimpan/memperbarui data ${title.toLowerCase()}.`,
                severity: "error",
            });
        }
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportExcel = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: "array" });

                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);

                const tbRef = ref(database, dbPath);
                const snapshot = await onValuePromise(tbRef);
                const existingData = snapshot.exists() ? snapshot.val() : {};
                const existingFieldValues = Object.values(existingData).map(item => item[fieldName]?.toLowerCase().trim());
                
                let importedCount = 0;
                let ignoredCount = 0;
                
                for (const item of jsonData) {
                    let importedValue = item[title.slice(title.indexOf(" ") + 1)];
                    if (!importedValue) {
                        ignoredCount++;
                        continue;
                    }
                    
                    const valueLower = importedValue.toLowerCase().trim();
                    if (existingFieldValues.includes(valueLower)) {
                        ignoredCount++;
                        continue;
                    }
                    
                    const newRef = push(tbRef);
                    const key = newRef.key;
                    const dataToSave = {
                        [primaryKeyField]: key,
                        [fieldName]: importedValue,
                    };
                    await set(newRef, dataToSave);
                    importedCount++;
                }
                
                setSnackbar({
                    open: true,
                    message: `Berhasil impor ${importedCount} data baru. ${ignoredCount} data sudah ada/invalid diabaikan.`,
                    severity: "success",
                });
            } catch (error) {
                console.error("Gagal impor Excel:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal mengimpor data dari Excel.",
                    severity: "error",
                });
            } finally {
                event.target.value = null;
            }
        };
        reader.readAsArrayBuffer(file);
    };

const exportToExcel = () => {
    if (!dataList || dataList.length === 0) {
        setSnackbar({ 
            open: true, 
            message: "Tidak ada data untuk diekspor.", 
            severity: "warning" 
        });
        return;
    }

    const formattedData = dataList.map((item) => {
        const row = {};
        row[primaryKeyField] = item[primaryKeyField];
        row["Index"] = item.index;
        row[title.slice(title.indexOf(" ") + 1)] = item[fieldName];
        
        const multiParentConfigs = getMultiParentConfig();
        if (multiParentConfigs.length > 0) {
            row["Nama Form"] = item.nama_form || "Tidak Ditemukan";
            
            multiParentConfigs.forEach(config => {
                if (item[config.labelField]) {
                    row[config.labelField] = item[config.labelField];
                }
            });
            row["Properti Field"] = item.properti_field;
            row["Data Type"] = item.data_type;
            row["Is Formula"] = item.is_formula ? "True" : "False";
            row["Formula"] = item.formula;
        }
        return row;
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, title.replace("Daftar ", ""));
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `${title.replace("Daftar ", "Data_")}.xlsx`);
};

    const handleMenuClick = (event, row) => {
        setMenuAnchor(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedRow(null);
    };

const handleEdit = (row) => {
    handleMenuClose();
    setIsEditing(true);
    setCurrentId(row[primaryKeyField]);
    setFieldValue(row[fieldName]);
    setIndexValue(row.index || "");

    // Set nilai Nama Form saat mode edit
    if (title.includes("Field Item Form") && row.id_form) {
        const namaFormOption = namaFormOptions.find(option => option.id === row.id_form);
        setSelectedNamaForm(namaFormOption || null);
    } else {
        setSelectedNamaForm(null);
    }

    // Set nilai Autocomplete saat mode edit (multi-parent)
    const multiParentConfigs = getMultiParentConfig();
    if (multiParentConfigs.length > 0) {
        const newParentValues = {};
        multiParentConfigs.forEach(config => {
            const parentId = row[config.idField];
            // Gunakan parentOptions yang sudah diformat untuk mencari label yang sesuai
            const parentOption = parentOptions[config.key]?.find(opt => opt?.id === parentId);
            if (parentOption) {
                newParentValues[config.key] = { id: parentId, label: parentOption.label };
            } else {
                newParentValues[config.key] = null;
            }
        });
        setParentValues(newParentValues);
        
        setPropertiFieldValue(row.properti_field || "");
        setUseFieldItemForm(false);
        setDataTypeValue(row.data_type || "Text");
        setIsFormulaValue(row.is_formula ? "True" : "False");
        setFormulaValue(row.formula || "");
    }
    setOpenDialog(true);
};

    const handleDelete = (row) => {
        handleMenuClose();
        setSelectedRow(row);
        setOpenDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (!selectedRow) return;
        
        try {
            const idToDelete = selectedRow[primaryKeyField];
            const itemRef = ref(database, `${dbPath}/${idToDelete}`);
            await remove(itemRef);
            setSnackbar({
                open: true,
                message: `${title.slice(title.indexOf(" ") + 1)} berhasil dihapus.`,
                severity: "success",
            });
            setOpenDeleteDialog(false);
            setSelectedRow(null);
        } catch (error) {
            console.error("Gagal hapus:", error);
            setSnackbar({
                open: true,
                message: `Gagal menghapus data ${title.toLowerCase()}.`,
                severity: "error",
            });
        }
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedRow(null);
    };

    const handleUseFieldItemFormChange = (event) => {
        const isChecked = event.target.checked;
        setUseFieldItemForm(isChecked);
        if (isChecked) {
            const formattedText = fieldValue.toLowerCase().replace(/ /g, '_');
            setPropertiFieldValue(formattedText);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'inherit' }}>
                <CircularProgress />
            </Box>
        );
    }

    const multiParentConfigs = getMultiParentConfig();

    return (
        <Box>
            <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1 }}>
                <Typography variant="h6">{title}</Typography>
                <Stack direction="row" spacing={1}>
                    <IconButton color="primary" size="small" onClick={handleOpenDialog}>
                        <AddIcon />
                    </IconButton>
                    <IconButton color="primary" size="small" onClick={handleImportButtonClick}>
                        <FileUploadOutlinedIcon />
                    </IconButton>
                    <input
                        type="file"
                        accept=".xlsx, .xls"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleImportExcel}
                    />
                    <IconButton color="primary" size="small" onClick={exportToExcel}>
                        <FileDownloadOutlinedIcon />
                    </IconButton>
                </Stack>
            </Stack>

            <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader>

<TableHead>
    <TableRow>
        <TableCell>Aksi</TableCell>
        <TableCell>Id</TableCell>
        <TableCell>Index</TableCell>
        <TableCell>{title.slice(title.indexOf(" ") + 1)}</TableCell>
        {multiParentConfigs.length > 0 && (
            <>
                <TableCell>Sub Kategori</TableCell>
                <TableCell>Kategori</TableCell>
                <TableCell>Nama Form</TableCell>
                <TableCell>Properti Field</TableCell>
                <TableCell>Tipe Data</TableCell>
                <TableCell>Is Formula</TableCell>
                <TableCell>Formula</TableCell>
            </>
        )}
    </TableRow>
</TableHead>
<TableBody>
    {dataList.length > 0 ? (
        dataList
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row) => (
                <TableRow key={row[primaryKeyField]}>
                    <TableCell>
                        <IconButton size="small" onClick={(event) => handleMenuClick(event, row)}>
                            <MoreVertIcon />
                        </IconButton>
                    </TableCell>
                    <TableCell>{row[primaryKeyField]}</TableCell>
                    <TableCell>{row.index || "-"}</TableCell>
                    <TableCell>{row[fieldName]}</TableCell>
                    {multiParentConfigs.length > 0 && (
                        <>
                            <TableCell>{row.nama_sub_kategori_form || "-"}</TableCell>
                            <TableCell>{row.nama_kategori_form || "Tidak Ditemukan"}</TableCell>
                            <TableCell>{row.nama_form || "Tidak Ditemukan"}</TableCell>
                            <TableCell>{row.properti_field}</TableCell>
                            <TableCell>{row.data_type}</TableCell>
                            <TableCell>{row.is_formula ? "True" : "False"}</TableCell>
                            <TableCell>{row.formula}</TableCell>
                        </>
                    )}
                </TableRow>
            ))
    ) : (
        <TableRow>
            <TableCell colSpan={multiParentConfigs.length > 0 ? 11 : 4} align="center">
                Tidak ada data {title.slice(title.indexOf(" ") + 1).toLowerCase()}
            </TableCell>
        </TableRow>
    )}
</TableBody>

                </Table>
            </TableContainer>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => handleEdit(selectedRow)}>
                    <EditIcon fontSize="small" sx={{ mr: 2 }} />
                    Edit
                </MenuItem>
                <MenuItem onClick={() => handleDelete(selectedRow)}>
                    <DeleteIcon fontSize="small" sx={{ mr: 2 }} />
                    Hapus
                </MenuItem>
            </Menu>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                <Typography variant="body2">
                    {`${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, dataList.length)} of ${dataList.length}`}
                </Typography>
                <TablePagination
                    component="div"
                    count={dataList.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 20]}
                    labelRowsPerPage=""
                    labelDisplayedRows={() => ""}
                    sx={{
                        ".MuiTablePagination-toolbar": {
                            padding: 0,
                            minHeight: "auto",
                        },
                        ".MuiTablePagination-actions": {
                            display: "block",
                        },
                        ".MuiTablePagination-selectLabel": {
                            display: "none",
                        },
                        ".MuiTablePagination-displayedRows": {
                            display: "none",
                        },
                        ".MuiTablePagination-select": {
                            marginLeft: "8px",
                            marginRight: "8px",
                        },
                    }}
                />
            </Box>

            {/* Dialog Tambah/Edit */}
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="md">
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                    {isEditing ? `Edit ${title.slice(title.indexOf(" ") + 1)}` : `Tambah ${title.slice(title.indexOf(" ") + 1)}`}
                    <IconButton aria-label="close" onClick={handleCloseDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {(title.includes("Field Item Form")) && (
                        <TextField
                            margin="normal"
                            label="Index"
                            type="number"
                            value={indexValue}
                            onChange={handleIndexChange}
                            fullWidth
                            sx={{ mb: 2 }}
                            inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*'
                            }}
                        />
                    )}

                    {/* Dropdown Nama Form - HANYA UNTUK FIELD ITEM FORM */}
                <Autocomplete
                    id="nama-form-autocomplete"
                    options={namaFormOptions}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    value={selectedNamaForm}
                    onChange={(event, newValue) => setSelectedNamaForm(newValue)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            margin="dense"
                            label="Nama Form"
                            fullWidth
                            required={title.includes("Field Item Form")}
                        />
                    )}
                    sx={{ mb: 2 }}
                />
                    
                    {multiParentConfigs.length > 0 && (
                        multiParentConfigs.map((config) => (
                            <Autocomplete
                                key={config.key}
                                id={`parent-autocomplete-${config.key}`}
                                options={parentOptions[config.key] || []}
                                getOptionLabel={(option) => option?.label || "None"}
                                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                value={parentValues[config.key] || null}
                                onChange={(event, newValue) => setParentValues(prev => ({
                                    ...prev,
                                    [config.key]: newValue
                                }))}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        margin="dense"
                                        label={config.label}
                                        fullWidth
                                    />
                                )}
                                sx={{ mb: 2 }}
                            />
                        ))
                    )}
                    
                    <TextField
                        autoFocus
                        margin="dense"
                        label={title.slice(title.indexOf(" ") + 1)}
                        type="text"
                        fullWidth
                        value={fieldValue}
                        onChange={(e) => {
                            setFieldValue(e.target.value);
                            if (useFieldItemForm) {
                                const formattedText = e.target.value.toLowerCase().replace(/ /g, '_');
                                setPropertiFieldValue(formattedText);
                            }
                        }}
                        sx={{ mb: 2 }}
                    />
                    
                    {title.includes("Field Item Form") && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <TextField
                                margin="dense"
                                label="Properti Field"
                                type="text"
                                fullWidth
                                value={propertiFieldValue}
                                onChange={(e) => setPropertiFieldValue(e.target.value)}
                                disabled={useFieldItemForm}
                                sx={{ mr: 2 }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={useFieldItemForm}
                                        onChange={handleUseFieldItemFormChange}
                                    />
                                }
                                label="Gunakan Field Item Form"
                                sx={{ whiteSpace: 'nowrap' }}
                            />
                        </Box>
                    )}
                    
                    {title.includes("Field Item Form") && (
                        <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                            <InputLabel id="data-type-label">Data Type</InputLabel>
                            <Select
                                labelId="data-type-label"
                                id="data-type-select"
                                value={dataTypeValue}
                                label="Data Type"
                                onChange={(e) => setDataTypeValue(e.target.value)}
                            >
                                <MenuItem value={"Text"}>Text</MenuItem>
                                <MenuItem value={"Number"}>Number</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                    
                    {title.includes("Field Item Form") && (
                        <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                            <InputLabel id="is-formula-label">Is Formula</InputLabel>
                            <Select
                                labelId="is-formula-label"
                                id="is-formula-select"
                                value={isFormulaValue}
                                label="Is Formula"
                                onChange={(e) => setIsFormulaValue(e.target.value)}
                            >
                                <MenuItem value={"False"}>False</MenuItem>
                                <MenuItem value={"True"}>True</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                    
                    {title.includes("Field Item Form") && isFormulaValue === "True" && (
                        <TextField
                            margin="dense"
                            label="Formula"
                            type="text"
                            fullWidth
                            value={formulaValue}
                            onChange={(e) => setFormulaValue(e.target.value)}
                        />
                    )}
                    
                </DialogContent>
                <DialogActions sx={{ pt: "20px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseDialog}>Batal</Button>
                    <Button onClick={handleSave} variant="contained">
                        {isEditing ? "Update" : "Simpan"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Konfirmasi Hapus */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Konfirmasi Hapus</DialogTitle>
                <DialogContent>
                    <Typography>
                        Apakah Anda yakin ingin menghapus {title.slice(title.indexOf(" ") + 1).toLowerCase()} "{selectedRow?.[fieldName]}"?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ pt: "0px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseDeleteDialog}>Batal</Button>
                    <Button onClick={confirmDelete} variant="contained" color="error">
                        Hapus
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default FieldItemFormConfig;